import React, { useEffect, useState } from 'react';
import { Box, Popover, Typography, makeStyles } from '@material-ui/core';
import EmojiEventsIcon from '@material-ui/icons/EmojiEvents';

import usePermission from '../../hooks/usePermission';
import { getTeamGoalProgress } from '../../services/crmService';
import { formatCurrencyFromCents } from '../../utils/currency';
import { APP_FONT_FAMILY } from '../../styles/typography';

const useStyles = makeStyles((theme) => ({
  chip: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    cursor: 'pointer',
    padding: '5px 12px',
    borderRadius: 20,
    background: '#f1f5f9',
    transition: 'background 0.2s',
    '&:hover': { background: '#e2e8f0' },
    [theme.breakpoints.down('xs')]: { display: 'none' },
  },
  pct: { fontWeight: 700, fontSize: '0.82rem' },
  track: { width: 56, height: 6, borderRadius: 6, background: '#e2e8f0', overflow: 'hidden' },
  fill: { height: '100%', borderRadius: 6, transition: 'width 0.6s ease' },
  pop: { padding: 16, minWidth: 240, fontFamily: APP_FONT_FAMILY },
  popTitle: { fontWeight: 700, color: '#0f172a', fontSize: '0.9rem', marginBottom: 10 },
  popRow: {
    display: 'flex',
    justifyContent: 'space-between',
    fontSize: '0.85rem',
    color: '#64748b',
    marginBottom: 6,
  },
  trackBig: {
    height: 10,
    borderRadius: 8,
    background: '#e2e8f0',
    overflow: 'hidden',
    margin: '8px 0',
  },
  popCaption: { fontSize: '0.82rem', color: '#64748b' },
}));

export default function TeamGoalChip() {
  const classes = useStyles();
  const { allowed } = usePermission('crm:read');
  const [data, setData] = useState(null);
  const [anchor, setAnchor] = useState(null);

  useEffect(() => {
    if (!allowed) return undefined;
    let active = true;
    const load = () =>
      getTeamGoalProgress()
        .then(({ data: d }) => {
          if (active) setData(d);
        })
        .catch(() => {});
    load();
    const id = setInterval(load, 300000); // atualiza a cada 5 min
    return () => {
      active = false;
      clearInterval(id);
    };
  }, [allowed]);

  if (!allowed || !data || !data.enabled || !data.target) return null;

  const pct = Math.min(data.progress || 0, 100);
  const reached = data.progress >= 100;
  const color = reached ? '#16a34a' : '#3b82f6';
  const remaining = Math.max(data.target - data.realized, 0);

  return (
    <>
      <Box
        className={classes.chip}
        onClick={(e) => setAnchor(e.currentTarget)}
        title="Meta da equipe"
      >
        <EmojiEventsIcon style={{ fontSize: 18, color }} />
        <Typography className={classes.pct} style={{ color }}>
          {data.progress}%
        </Typography>
        <Box className={classes.track}>
          <Box className={classes.fill} style={{ width: `${pct}%`, background: color }} />
        </Box>
      </Box>
      <Popover
        open={Boolean(anchor)}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Box className={classes.pop}>
          <Typography className={classes.popTitle}>Meta da equipe — este mês</Typography>
          <Box className={classes.popRow}>
            <span>Realizado</span>
            <strong style={{ color }}>{formatCurrencyFromCents(data.realized)}</strong>
          </Box>
          <Box className={classes.popRow}>
            <span>Meta</span>
            <strong style={{ color: '#0f172a' }}>{formatCurrencyFromCents(data.target)}</strong>
          </Box>
          <Box className={classes.trackBig}>
            <Box className={classes.fill} style={{ width: `${pct}%`, background: color }} />
          </Box>
          <Typography className={classes.popCaption}>
            {reached
              ? '🎉 Meta atingida!'
              : `${data.progress}% da meta · faltam ${formatCurrencyFromCents(remaining)}`}
          </Typography>
        </Box>
      </Popover>
    </>
  );
}
